import { Application } from "pixi.js";
import { GameState, BottleId, LevelConfig } from "../core/types";
import { computePourPlan, applyPour, undoMove, checkWin } from "../core/rules";
import { generateLevel } from "../core/generator";
import { LEVELS } from "../core/levelConfigs";
import { GameStateMachine } from "./gameStateMachine";
import { getCurrentLevel, setCurrentLevel, updateLevelProgress, saveGameState, loadGameState, clearGameState } from "./storage";
import { Renderer, PACK_ANIMATION } from "../render/Renderer";

export interface GameControllerOptions {
  app: Application;
}

export class GameController {
  private _app: Application;
  private stateMachine: GameStateMachine;
  private currentLevel: number;
  private currentConfig: LevelConfig;
  private isPaused: boolean = false;
  private renderer: Renderer;


  constructor(options: GameControllerOptions) {
    this._app = options.app;
    this.currentLevel = getCurrentLevel();
    this.currentConfig = this.getLevelConfig(this.currentLevel);
    
    const initialState = this.loadOrGenerateLevel();
    this.stateMachine = new GameStateMachine(initialState);
    
    this.renderer = new Renderer(this._app);
    this.setupRendererCallbacks();
    this.render();
    this.setupEventListeners();
    this.setupInput();
  }

  private setupRendererCallbacks(): void {
    this.renderer.setButtonCallback("restart", () => {
      this.restart();
      this.render();
    });
    this.renderer.setButtonCallback("nextLevel", () => {
      if (this.stateMachine.getCurrentPhase() === "win") {
        this.nextLevel();
      }
    });
  }

  private render(): void {
    this.renderer.render(this.stateMachine.getState(), this.stateMachine.getSelectedBottleId(), this.stateMachine.getCurrentPhase());
  }

  private pointerDownPos: { x: number; y: number } | null = null;

  private setupInput(): void {
    const stage = this._app.stage;
    stage.eventMode = "static";
    stage.hitArea = this._app.screen;

    stage.on("pointerdown", (event: any) => {
      const localPos = event.getLocalPosition(stage);
      this.pointerDownPos = { x: localPos.x, y: localPos.y };
    });

    stage.on("pointerup", (event: any) => {
      if (!this.pointerDownPos) return;
      const localPos = event.getLocalPosition(stage);
      const dx = Math.abs(localPos.x - this.pointerDownPos.x);
      const dy = Math.abs(localPos.y - this.pointerDownPos.y);
      this.pointerDownPos = null;

      if (dx < 10 && dy < 10) {
        this.handleTap(localPos.x, localPos.y);
      }
    });

    stage.on("pointerupoutside", () => {
      this.pointerDownPos = null;
    });
  }

  private handleTap(x: number, y: number): void {
    const state = this.stateMachine.getState();
    const config = this.renderer["layout"].getConfig();
    const { width: visualWidth, height: visualHeight, offsetX, offsetY } = this.renderer["layout"].getBottleVisualMetrics();

    for (let i = 0; i < state.bottles.length; i++) {
      const pos = this.renderer["layout"].getBottlePosition(i, state.bottles.length, state.packing.slots.length);
      const width = visualWidth;
      const height = visualHeight;
      const hitPadding = config.isMobile ? 14 : 10;

      const hitX = pos.x + offsetX;
      const hitY = pos.y + offsetY;

      if (x >= hitX - hitPadding && x <= hitX + width + hitPadding &&
          y >= hitY - hitPadding && y <= hitY + height + hitPadding) {
        this.selectBottle(state.bottles[i].id);
        this.render();
        return;
      }
    }
  }

  private getLevelConfig(level: number): LevelConfig {
    const config = LEVELS.find(l => l.level === level);
    if (!config) {
      throw new Error(`Level ${level} not found`);
    }
    return config;
  }

  private loadOrGenerateLevel(): GameState {
    const savedState = loadGameState();
    if (savedState && savedState.level === this.currentLevel) {
      return savedState;
    }

    clearGameState();

    return generateLevel(this.currentConfig);
  }

  private setupEventListeners(): void {
    this.stateMachine.on("win", (_event) => {
      this.handleWin();
    });

    this.stateMachine.on("pack_complete", (_event) => {
      this.checkWinCondition();
    });
  }

  selectBottle(bottleId: BottleId): boolean {
    if (this.isPaused) return false;

    const state = this.stateMachine.getState();
    const selectedId = this.stateMachine.getSelectedBottleId();

    if (selectedId === null) {
      this.stateMachine.setSelectedBottleId(bottleId);
      this.stateMachine.dispatch({ type: "bottle_selected", bottleId });
      this.render();
      return true;
    }

    if (selectedId === bottleId) {
      this.stateMachine.setSelectedBottleId(null);
      this.stateMachine.dispatch({ type: "bottle_deselected" });
      this.render();
      return true;
    }

    const result = computePourPlan(state, selectedId, bottleId);
    if (result.ok) {
      this.stateMachine.dispatch({ type: "pour_start", from: selectedId, to: bottleId });
      this.executePour(result.value!);
      return true;
    }

    this.stateMachine.setSelectedBottleId(bottleId);
    this.stateMachine.dispatch({ type: "bottle_selected", bottleId });
    this.render();
    return true;
  }

  private executePour(plan: any): void {
    const state = this.stateMachine.getState();
    
    // Apply pour with seal/pack enabled - this handles all game logic automatically
    const moveRecord = applyPour(state, plan, true);
    
    // Check if pack happened
    
    // Trigger animations for seal/pack
    if (moveRecord.pack) {
      const packDelay = PACK_ANIMATION.delayMs;

      // Flash the slot
      this.renderer.flashSlot(moveRecord.pack.slotIndex);
      
      // Animate the packed bottle
      setTimeout(() => {
        this.renderer.animatePackAway(moveRecord.pack!.bottleId, moveRecord.pack!.slotIndex);
      }, packDelay);
    }

    this.stateMachine.dispatch({ type: "pour_complete", from: plan.from, to: plan.to });
    this.stateMachine.setSelectedBottleId(null);
    saveGameState(state);
    
    // Check win condition after pack
    if (moveRecord.pack) {
      const packTotal = PACK_ANIMATION.delayMs + PACK_ANIMATION.durationMs;
      setTimeout(() => {
        this.checkWinCondition();
      }, packTotal);
    }

    // Delay render for animation
    setTimeout(() => {
      this.render();
    }, moveRecord.pack ? PACK_ANIMATION.delayMs + PACK_ANIMATION.durationMs : 0);
  }

  undo(): boolean {
    if (this.isPaused) return false;

    const state = this.stateMachine.getState();
    if (state.moves.length === 0) return false;

    const success = undoMove(state);
    if (success) {
      this.stateMachine.dispatch({ type: "undo" });
      this.stateMachine.setSelectedBottleId(null);
      saveGameState(state);
      this.render();
    }

    return success;
  }

  restart(): void {
    const state = this.stateMachine.getState();
    const newConfig = this.getLevelConfig(this.currentLevel);
    const newState = generateLevel(newConfig, state.seed);

    this.stateMachine.reset(newState);
    this.stateMachine.dispatch({ type: "restart" });
    clearGameState();
    this.render();
  }

  private checkWinCondition(): void {
    const state = this.stateMachine.getState();
    if (checkWin(state)) {
      this.stateMachine.dispatch({ type: "win" });
      // Force render to show win screen
      this.render();
    }
  }

  private handleWin(): void {
    const state = this.stateMachine.getState();
    updateLevelProgress(state.level, state.score, state.moves.length);
    clearGameState();
    // Force re-render to show win screen
    this.render();
  }

  goToLevel(level: number): void {
    if (level < 1 || level > LEVELS.length) {
      throw new Error(`Invalid level: ${level}`);
    }

    this.currentLevel = level;
    setCurrentLevel(level);
    this.currentConfig = this.getLevelConfig(level);

    const newState = generateLevel(this.currentConfig);
    this.stateMachine.reset(newState);
    this.stateMachine.dispatch({ type: "level_start", level });
    clearGameState();
    this.render();
  }

  nextLevel(): void {
    if (this.currentLevel < LEVELS.length) {
      this.goToLevel(this.currentLevel + 1);
    }
  }

  previousLevel(): void {
    if (this.currentLevel > 1) {
      this.goToLevel(this.currentLevel - 1);
    }
  }

  pause(): void {
    if (!this.isPaused) {
      this.isPaused = true;
      this.stateMachine.dispatch({ type: "pause" });
    }
  }

  resume(): void {
    if (this.isPaused) {
      this.isPaused = false;
      this.stateMachine.dispatch({ type: "resume" });
    }
  }

  togglePause(): void {
    if (this.isPaused) {
      this.resume();
    } else {
      this.pause();
    }
  }

  getState(): GameState {
    return this.stateMachine.getState();
  }

  getCurrentLevel(): number {
    return this.currentLevel;
  }

  getCurrentPhase(): string {
    return this.stateMachine.getCurrentPhase();
  }

  getSelectedBottleId(): BottleId | null {
    return this.stateMachine.getSelectedBottleId();
  }

  isGamePaused(): boolean {
    return this.isPaused;
  }

  destroy(): void {
    this.renderer.destroy();
    this.stateMachine.reset(this.stateMachine.getState());
  }
}
